import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators, UntypedFormArray } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Guid } from 'guid-typescript';
import { Code } from 'src/app/core/model/dictionary';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { sortBy } from 'lodash';

@Component({
  standalone: false,
  selector: 'app-formly-designer-flag-edit',
  templateUrl: './formly-designer-flag-edit.component.html',
  styleUrls: ['./formly-designer-flag-edit.component.scss']
})
export class FormlyDesignerFlagEditComponent implements OnInit
{
  @Input() public fields: FormlyFieldConfig[];
  @Input() public isDeleted = false;

  @Output() public formChanged: EventEmitter<any> = new EventEmitter();

  public editForm: any;
  public flags: UntypedFormArray;
  public flagCategories: Code[];
  public flagPriorities: Code[];
  public standardFlags: Code[];

  /**
   * Current search term entered from a comboxbox
   */
  public searchTerm: string;

  /**
   * Maximum number of items for flag virtualization. This is to accelerate the DOM performance.
   */
  public virtualFlag: any = {
    itemHeight: 30
  };

  public loaded = false;

  constructor(
    private fb: UntypedFormBuilder,
    private dictionaryService: DictionaryService) { }

  @Input() public set model(flags: any)
  {
    this.dictionaryService.getFlagLookups().subscribe({
      next: (responseList) =>
      {
        this.flagPriorities = sortBy(responseList[0], ['displayName']);
        this.flagCategories = sortBy(responseList[1], ['displayName']);
        this.standardFlags = sortBy(responseList[2], ['displayName']);
        this.initModel(flags);
      }
    });
  }

  public ngOnInit(): void
  {
    this.loaded = false;
  }

  /**
   * Add a flag entry
   */
  public addFlag(id: number = null, version = 0, description = '', category: any = null, priority: any = null,
    privacy = true, query: any = null, showRules = true, importError = false): void
  {
    this.editForm.controls.flags.push(this.initFlag(id, version, description, category, priority, privacy, query, showRules, importError));
    if (id === null)
    {
      this.editForm.markAsDirty();
    }

    // Set the proper flags to load based on the category
    const controlIndex = this.editForm.controls.flags.controls.length - 1;
    this.getFlags(category, null, controlIndex);
  }

  /**
   * Flag category is changed (from html page)
   *
   * @param event Categeory event
   * @param flagControl Flag control
   * @param index Item index
   */
  public categoryChanged(event: any, flagControl: any, index: number): void
  {
    this.getFlags(event, flagControl, index);
    if (event === undefined)
    {
      this.clearFlags(index, flagControl);
    }
  }

  /**
   * Remove a flag entry
   *
   * @param index Entry index
   */
  public removeFlag(index: number): void
  {
    const control = this.editForm.controls.flags;
    control.removeAt(index);
    this.editForm.markAsDirty();
  }

  public showRulesChanged(e: any): void
  {
    this.editForm.controls.flags.controls[e.index].controls.showRules.value = e.show;
    // If the rule is opened, we assumed a state changes.
    if (!this.isDeleted)
    {
      this.editForm.markAsDirty();
      this.formChanged.emit(this.editForm);
    }
  }

  /**
   * Used for the trackBy to prevent rendering the DOM and losing field focus.
   *
   * @param index Row index.
   * @param item Row item.
   */
  public flagsTrackBy(index: number, item: any): string
  {
    return item.value.guid;
  }

  /**
   * Filters the flags based on user input.
   */
  public filterFlags(filterText: string, index: number): void
  {
    this.searchTerm = filterText;
    const filteredFlags = this.editForm.controls.flags.controls[index].get('allFlags').value.filter(
      (flag: any) => flag.displayName.toLowerCase().includes(filterText.toLowerCase()));
    this.editForm.controls.flags.controls[index].get('allFlagsFiltered').setValue(filteredFlags);
  }

  /**
   * Load the flags list based on the selected category and dictionary assigned or standard
   *
   * @param event Control event (category)
   * @param flagControl Flag control
   * @param index Row item index
   */
  private getFlags(event: any, flagControl: any, index: number)
  {
    if (event && event !== undefined && event.id !== undefined)
    {
      if (event.codeSetPerRecord != null)
      {
        this.editForm.controls.flags.controls[index].get('currentDictionaryUsed').setValue(1);
        this.dictionaryService.getCodesByCodeSetId(event.codeSetPerRecord.id).subscribe({
          next: (data) =>
          {
            this.editForm.controls.flags.controls[index].get('allFlags').setValue(sortBy(data, ['displayName']));
            this.editForm.controls.flags.controls[index].get('allFlagsFiltered').setValue(sortBy(data, ['displayName']));
            this.setControlState(event, index);
          }
        });
      }
      else
      {
        this.editForm.controls.flags.controls[index].get('currentDictionaryUsed').setValue(2);
        this.editForm.controls.flags.controls[index].get('allFlags').setValue(this.standardFlags);
        this.editForm.controls.flags.controls[index].get('allFlagsFiltered').setValue(this.standardFlags);
        this.setControlState(event, index);
      }

      // Reset only if the dictionary has changed and not called from initial load (control will equal null on initial load)
      if (flagControl)
      {
        if ((this.editForm.controls.flags.controls[index].get('currentDictionaryUsed').value !==
          this.editForm.controls.flags.controls[index].get('previousDictionaryUsed').value) ||
          (this.editForm.controls.flags.controls[index].get('currentDictionaryUsed').value === 1))
        {
          this.clearFlags(index, flagControl);
        }
      }
      this.editForm.controls.flags.controls[index].get('previousDictionaryUsed').setValue(
        this.editForm.controls.flags.controls[index].get('currentDictionaryUsed').value);
    }
    else
    {
      this.setControlState(event, index);
    }
  }

  /**
   * Set flag control state
   *
   * @param category Category control
   * @param index Item idex.
   */
  private setControlState(category: any, index: number): void
  {
    if (category !== null && category !== undefined && category !== '')
    {
      this.editForm.controls.flags.controls[index].get('description').enable();
    }
    else
    {
      this.editForm.controls.flags.controls[index].get('description').disable();
      this.clearFlags(index, null);
    }
    // this.editForm.controls.flags.controls[index].get('description').updateValueAndValidity();
  }

  /**
   * Clear the flag on category changes
   *
   * @param index Item index
   * @param flagControl Flag control
   */
  private clearFlags(index: number, flagControl: any): void
  {
    this.editForm.controls.flags.controls[index].get('description').value = null;
    if (flagControl)
    {
      flagControl.reset();
    }
  }

  /**
   * Model initilization
   *
   * @param flags Assessment flags edit object.
   */
  private initModel(flags: any): void
  {
    if (flags === undefined || this.loaded)
    {
      return;
    }

    // Build form
    this.editForm = this.fb.group({
      flags: this.fb.array([])
    });

    if (flags && flags.length > 0)
    {
      flags.forEach(data =>
      {
        this.addFlag(data.id, data.version, data.description, data.category, data.priority,
          data.privacy, data.query, false, data.importError);
      });
    }

    this.editForm.valueChanges.subscribe({
      next: () =>
      {
        if (!this.isDeleted)
        {
          this.formChanged.emit(this.editForm);
        }
      }
    });

    // Must apply a delay here because isDeleted is not set yet when setting the model.
    setTimeout(() =>
    {
      this.disableControls();
      this.loaded = true;
    });
  }

  /**
   * Create a form group.
   */
  private initFlag(id: number, version: number, description: string, category: any, priority: any,
    privacy: boolean, query: any, showRules: boolean, importError: boolean): UntypedFormGroup
  {
    if (query === null)
    {
      query = {
        condition: 'and',
        rules: []
      };
    }
    return this.fb.group({
      guid: Guid.create().toString(),
      id: [id],
      version: [version],
      description: [description, Validators.required],
      category: [category, Validators.required],
      priority: [priority, Validators.required],
      privacy: [privacy, Validators.required],
      query: [query, Validators.required],
      showRules: [showRules],
      importError: [importError],
      allFlags: [{ value: [] }],
      allFlagsFiltered: [{ value: [] }],
      previousDictionaryUsed: null,
      currentDictionaryUsed: null
    });
  }

  /**
   * Disable controls when Assessement is deleted
   */
  private disableControls(): void
  {
    if (this.isDeleted)
    {
      this.editForm.controls.flags.controls.forEach(data =>
      {
        data.controls.description.disable();
        data.controls.privacy.disable();
        data.controls.priority.disable();
        data.controls.category.disable();
      });
    }
  }
}
